import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import { useAuth } from "../utils/auth";
import { sdkWrapperURL } from "../utils/api-url-list";
import { getCurrentLocation } from "../utils/geoLocation";
import { constants } from "../utils/ranges";
import LoadingScreen from "../components/LoadingScreen";
import ProgressCircle from "../components/ProgressCircle";
import BannerWoman from "../assets/images/BannerWoman.png";
import { ReactComponent as ConnectToDoctorsBanner } from "../assets/icons/ConnectToDoctorsBanner.svg";
import { ReactComponent as Arrow } from "../assets/icons/Arrow.svg";
// import { ReactComponent as Check } from "../assets/icons/Check.svg";
import { ReactComponent as Chevron } from "../assets/icons/Chevron.svg";

const Dashboard = () => {
  const navigate = useNavigate();
  const {
    getToken,
    userData: { employee_id, height, weight },
  } = useAuth();
  const [loading, setLoading] = useState(true);
  const [lastScanResult, setLastResult] = useState({});
  const selectedScoreRange = useMemo(
    () =>
      constants["Health Score"].ranges.reduce((p, c) =>
        lastScanResult.wellness_score > c.point ? c : p
      ),
    [lastScanResult.wellness_score]
  );
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);

  const connectToDoctors = async () => {
    setBtnLoading(true);
    try {
      const coords = await getCurrentLocation();
      const token = await getToken();
      const resp = await fetch(sdkWrapperURL("/users/add-saved-products"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          product_id: "eb62affe-382a-41eb-941c-a5cb0bbb2f29",
          interest_filtration: "ACCEPT",
          application_info: {
            referral_code: localStorage.getItem("referral") || "",
            geo_location: coords,
          },
        }),
      });
      const respJSON = await resp.json();
      if (respJSON.statusCode?.toString().startsWith("2"))
        setSuccessModalOpen(true);
      else throw new Error(respJSON.message);
    } catch (err) {
      console.error(err);
    } finally {
      setBtnLoading(false);
    }
  };

  const startScan = () => {
    const bmi = weight / (height / 100) ** 2;
    if (bmi < 10 || bmi > 100) {
      alert(
        `Invalid Height (${height}) or Weight (${weight}) entered. Please provide accurate measurements for correct computation.`
      );
      navigate("/my-account");
    } else navigate("/select-option");
  };

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    (async () => {
      try {
        const token = await getToken();
        const scoreResp = await fetch(
          sdkWrapperURL("/users/sdk/test_api_private"),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
            body: JSON.stringify({
              action: "list_scan_UL",
              employee_id,
              table_config_rows_per_page: 1,
              table_config_page_no: 1,
            }),
            signal: controller.signal,
          }
        );
        const scoreRespJSON = await scoreResp.json();
        if (
          scoreRespJSON?.statusCode?.toString().startsWith("2") &&
          scoreRespJSON?.result?.length > 0
        )
          setLastResult(scoreRespJSON.result[0]);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return loading ? (
    <LoadingScreen style={{ minHeight: "60vh" }} />
  ) : (
    <section className="px-6 py-2">
      <div className="px-6 flex items-center space-x-6">
        <ProgressCircle
          value={lastScanResult.wellness_score}
          color={selectedScoreRange.color}
          subtitle={selectedScoreRange.label}
        />
        <div>
          <p className="text-primary text-sm font-semibold">Health Score</p>
          <p className="text-primary/70 text-xxs whitespace-pre-line">
            {lastScanResult.created_at?.length > 0
              ? `Recorded on\n${new Date(
                  lastScanResult.created_at?.replace?.(" ", "T") ?? 0
                ).toLocaleDateString(undefined, {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })}`
              : "No scan data available"}
          </p>
        </div>
      </div>

      <button
        className="w-full my-6 flex items-center justify-between rounded-xl bg-gradient-to-r from-primary to-[#3545B9] text-white text-left"
        type="button"
        onClick={startScan}
      >
        <img
          className="self-end flex-shrink-0 h-20"
          src={BannerWoman}
          alt="Banner"
        />
        <div className="mx-1.5 space-y-1">
          <h3 className="text-xs font-semibold">Know Your Health</h3>
          <p className="text-xxs tracking-tighter leading-tight">
            Get 20+ vital parameters in just 60 seconds.
          </p>
        </div>
        <div className="flex-shrink-0 flex items-center space-x-1 rounded-full mr-3 px-2 py-1 bg-white text-primary">
          <span className="flex-shrink-0 text-xxs tracking-tighter font-medium">
            Start A Scan
          </span>
          <Arrow className="flex-shrink-0 h-2.5 w-2.5" />
        </div>
      </button>
      {lastScanResult.created_at?.length > 0 && (
        <Link
          to="/scan-result"
          state={{
            result: {
              entry_time: lastScanResult.created_at,
              scan_id: lastScanResult.scan_id,
              posture: lastScanResult.posture,
              wellness_score: lastScanResult.wellness_score,
              vitals: lastScanResult.vitals,
              metadata: {
                heart_scores: lastScanResult.heart_scores,
                cardiovascular: lastScanResult.cardiovascular,
                physiological_scores: lastScanResult.physiological_scores,
                glucose_info: lastScanResult.glucose_info,
              },
            },
          }}
          className="flex items-center justify-between py-4 px-6 rounded-xl bg-[#192852] text-white"
        >
          <span className="text-sm">View Assessment Result</span>
          <Chevron className="flex-shrink-0 h-4 w-4 -rotate-90" />
        </Link>
      )}
      <button
        className="group w-full my-6 flex items-center justify-between rounded-2xl bg-gradient-to-b from-[#3D5CB1]/5 via-[#8A9DD0]/[0.43] via-40% to-white text-left"
        type="button"
        onClick={connectToDoctors}
        disabled={btnLoading}
      >
        <div className="px-6 py-4">
          <h3 className="text-primary text-sm font-bold leading-tight tracking-tight">
            Connect with
            <br />
            Digital Health Educators
          </h3>
          <p className="mt-1.5 text-black/80 text-xxs leading-tight tracking-tight">
            Explore nearby healthcare
            <br />
            providers effortlessly.
          </p>
          <p className="max-w-40 mt-3 px-4 py-2 rounded-full bg-primary group-disabled:bg-darkgray text-white text-xs text-center">
            Request Callback
          </p>
        </div>
        <ConnectToDoctorsBanner className="shrink-0 self-end mr-4 grow basis-24 max-h-32" />
        <Transition show={isSuccessModalOpen} as={Fragment}>
          <Dialog as={Fragment} onClose={() => {}}>
            <Transition.Child
              className="fixed top-0 bottom-0 left-0 right-0 bg-black/70 flex items-center justify-center"
              enter="duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="duration-300 delay-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Transition.Child
                as={Fragment}
                enter="delay-150 duration-200"
                enterFrom="opacity-0 scale-50"
                enterTo="opacity-100 scale-100"
                leave="duration-100"
                leaveFrom="opacity-50 scale-50"
                leaveTo="opacity-0 scale-0"
              >
                <Dialog.Panel className="w-4/5 bg-white rounded-2xl p-6 flex flex-col items-center text-center">
                  <Dialog.Title className="text-primary text-sm font-medium">
                    Callback Request Placed
                  </Dialog.Title>
                  <Dialog.Description className="mt-2 text-black/80 text-xs">
                    We have successfully captured your request for a callback,
                    our team will contact you.
                  </Dialog.Description>
                  <button
                    className="mt-4 px-4 py-2 rounded-full outline-none bg-primary text-white text-xs font-medium"
                    type="button"
                    onClick={() => setSuccessModalOpen(false)}
                  >
                    Ok
                  </button>
                </Dialog.Panel>
              </Transition.Child>
            </Transition.Child>
          </Dialog>
        </Transition>
      </button>
      <div className="my-8">
        <h2 className="mb-4 text-primary text-sm font-semibold">
          About Digital Health Assessment
        </h2>
        <iframe
          className="rounded-xl w-full aspect-video outline-none bg-black"
          title="Product Video"
          src="https://www.youtube.com/embed/LbJ70TaXErA?color=white&playsinline=1&fs=0&disablekb=1&rel=0&cc_load_policy=0&iv_load_policy=3"
        />
      </div>
      <div className="my-8">
        <p className="text-darkgray text-xs text-justify">
          Our Digital Health Scan is like having a personal health check-up at
          your fingertips! With just a quick scan, you can monitor important
          vitals like heart rate, blood pressure, and more. Our Vitals
          Monitoring feature makes it easy to stay on top of your well-being, so
          you can feel confident and in control every step of the way.
        </p>
      </div>
    </section>
  );
};

export default Dashboard;
